import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { HourglassMedium, Paperclip } from "@phosphor-icons/react";
import {
  WaitingOnAttachButton,
  WaitingOnBannerContainer,
  WaitingOnBannerTitle,
  WaitingOnItemLabel,
  WaitingOnItemMeta,
  WaitingOnItemRow,
  WaitingOnStaleChip,
} from "../AIPane/AIPaneStyled";
import AttachToWorkItemModal from "./AttachToWorkItemModal";
import { isLiveReviewCopy } from "../../utils/reviewCopy";

/**
 * "Waiting on:" banner — the inbox knows what the matter is expecting.
 *
 * When the open document's matter has open (Sent) work items, whoever is
 * looking at this piece of paper sees what the matter is waiting to
 * receive — "Get statement — migraines (with Anne, 9d)" — and can attach
 * the document to the right item on the spot, which volleys it back to
 * the requester. Renders nothing at all when there's nothing waiting:
 * silence is the honest answer, and an inbox reader should never pay
 * chrome for an empty list.
 *
 * Presentational: `AIPane` owns the waiting-on fetch (one call feeds
 * this banner AND the action footer's one-door gate) and hands the
 * items down; `onAttached` asks it to refetch after a volley.
 */
const WaitingOnBanner = ({ documentId, items = [], onAttached = undefined }) => {
  const [attachItemId, setAttachItemId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleAttachClick = useCallback((workItemId) => {
    setAttachItemId(workItemId);
    setModalOpen(true);
  }, []);

  if (!documentId || items.length === 0) return null;

  return (
    <>
      <WaitingOnBannerContainer
        role="note"
        aria-label="Open work items waiting on documents"
        data-testid="waiting-on-banner"
      >
        <WaitingOnBannerTitle>
          <HourglassMedium size={13} weight="bold" />
          Waiting on
        </WaitingOnBannerTitle>
        {items.map((item) => (
          <WaitingOnItemRow key={item.workItemId}>
            <WaitingOnItemLabel title={item.subject}>
              {item.staleness && (
                <WaitingOnStaleChip $tone={item.staleness}>
                  {item.ageDays}d
                </WaitingOnStaleChip>
              )}
              {item.subject}
              <WaitingOnItemMeta>
                {item.holderName ? ` — with ${item.holderName}` : ""}
                {!item.staleness && item.ageDays != null
                  ? `, ${item.ageDays}d`
                  : ""}
              </WaitingOnItemMeta>
            </WaitingOnItemLabel>
            {/* A live document review's copy moves from the review, not
                from paper arriving — no Attach on it (utils/reviewCopy). */}
            {!isLiveReviewCopy(item) && (
              <WaitingOnAttachButton
                type="button"
                onClick={() => handleAttachClick(item.workItemId)}
                aria-label={`Attach this document to ${item.itemNumber}`}
              >
                <Paperclip size={12} weight="bold" />
                Attach
              </WaitingOnAttachButton>
            )}
          </WaitingOnItemRow>
        ))}
      </WaitingOnBannerContainer>

      <AttachToWorkItemModal
        open={modalOpen}
        documentId={documentId}
        initialWorkItemId={attachItemId}
        onClose={() => setModalOpen(false)}
        onAttached={onAttached}
      />
    </>
  );
};

WaitingOnBanner.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  items: PropTypes.arrayOf(PropTypes.object),
  onAttached: PropTypes.func,
};

export default WaitingOnBanner;
